import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, Eraser, Check, Upload, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { podApi } from '../lib/api';

export default function PodUploadDialog({ open, onClose, load, onSuccess }) {
  const [photo, setPhoto] = useState('');
  const [recipient, setRecipient] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [signature, setSignature] = useState('');
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    if (open) {
      setPhoto(''); setRecipient(''); setNotes(''); setSignature('');
      setTimeout(() => setupCanvas(), 100);
    }
  }, [open]);

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0B2545';
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDraw = () => {
    drawing.current = false;
    setSignature(canvasRef.current.toDataURL('image/png'));
  };

  const clearSig = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setSignature('');
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Please upload under 5MB.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!photo) { toast({ title: 'Photo required', description: 'Please capture a delivery photo.' }); return; }
    if (!recipient) { toast({ title: 'Recipient name', description: 'Please enter who received the goods.' }); return; }
    setUploading(true);
    try {
      await podApi.upload(load.id, { load_id: load.id, photo, signature, recipient_name: recipient, notes });
      toast({ title: 'PoD uploaded!', description: `Load ${load.id} marked as delivered. Invoice will be sent to shipper.` });
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally { setUploading(false); }
  };

  if (!open || !load) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-[#0B2545]">Upload Proof of Delivery</h3>
            <p className="text-xs text-gray-500 font-mono">Load {load.id}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <Label className="text-sm font-semibold">Delivery Photo *</Label>
            <p className="text-[11px] text-gray-500 mb-2">Photo of unloaded goods at destination</p>
            {photo ? (
              <div className="relative rounded-xl overflow-hidden border border-gray-200">
                <img src={photo} alt="PoD" className="w-full max-h-64 object-cover" />
                <button onClick={() => setPhoto('')} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <button onClick={() => fileInputRef.current?.click()} className="w-full h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-500 hover:border-[#00A651] hover:bg-[#00A651]/5">
                <Camera className="w-8 h-8 mb-1" />
                <span className="text-sm font-semibold">Take Photo</span>
                <span className="text-[11px]">or select from gallery</span>
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" />
          </div>

          <div>
            <Label className="text-sm font-semibold">Recipient Signature</Label>
            <p className="text-[11px] text-gray-500 mb-2">Ask the receiver to sign below</p>
            <div className="relative border border-gray-300 rounded-xl overflow-hidden bg-white">
              <canvas
                ref={canvasRef}
                onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
                className="w-full h-32 touch-none cursor-crosshair"
                style={{ background: 'repeating-linear-gradient(0deg, transparent 0, transparent 27px, #e5e7eb 27px, #e5e7eb 28px)' }}
              />
              {!signature && <p className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm pointer-events-none">Sign here</p>}
              <button onClick={clearSig} className="absolute top-2 right-2 text-xs text-gray-500 bg-white/80 px-2 py-1 rounded flex items-center gap-1"><Eraser className="w-3 h-3" />Clear</button>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold">Received by (Name)</Label>
            <Input placeholder="Ramesh Kumar" value={recipient} onChange={e => setRecipient(e.target.value)} className="mt-1.5" />
          </div>

          <div>
            <Label className="text-sm font-semibold">Notes (optional)</Label>
            <Textarea placeholder="e.g. Unloaded at gate 3, 18 T received in good condition" value={notes} onChange={e => setNotes(e.target.value)} className="mt-1.5" rows={3} />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white p-4 border-t border-gray-100 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={uploading} className="flex-1 bg-[#00A651] hover:bg-[#008a43] text-white">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Check className="w-4 h-4 mr-2" />Complete Delivery</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
